/* @jsx h */
import { defineComponent, ref, h } from "vue";
import { Order } from "@lemmonhq/types";
import { api } from "@app/data/api";
import { useToast } from "@app/vue/providers/Toast";
import { getOrderDisplayName } from "@lemmonhq/orders";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";

import { useShop } from "@app/vue/providers/ShopConnection";

interface AcceptOrderModalProps {
  onClose: () => void;
  order: Order;
  onConfirm: (order: Order) => void;
}

export const AcceptOrderModal = defineComponent<AcceptOrderModalProps>({
  name: "AcceptOrderModal",
  props: ["onClose", "onConfirm", "order"],
  setup(props) {
    const { t } = useLocalization();
    const estimatedTime = ref(15);
    const loading = ref(false);
    const toast = useToast();
    const shop = useShop();

    const handleSubmit = async (e: Event) => {
      e.preventDefault();
      loading.value = true;
      try {
        const { order, printJobs, payment } = await api.acceptOrder(
          props.order.id,
        );
        try {
          printJobs.forEach((job) => {
            shop.print(job);
          });
        } catch (e) {
          alert(t("orders.accept.print_failed"));
        }
        props.onConfirm(order);
        props.onClose();
        toast.showToast(t("orders.accept.success"), {
          type: "success",
          autoHide: 3000,
          icon: "check_circle",
        });
      } catch (error) {
        console.error("Failed to accept order:", error);
        toast.showToast(t("orders.accept.failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={t("orders.accept.title")}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="p-4 border-t ">
            <div class="flex justify-between gap-2">
              <button
                onClick={props.onClose}
                class="btn btn-error"
                type="button"
              >
                {t("orders.accept.cancel")}
              </button>
              <button
                onClick={handleSubmit}
                class="btn btn-primary"
                type="submit"
                disabled={loading.value}
              >
                {loading.value ? (
                  <span class="loading loading-spinner loading-sm"></span>
                ) : (
                  t("orders.accept.confirm")
                )}
              </button>
            </div>
          </div>
        )}
      >
        <div class="p-6 space-y-4">
          <div class="">
            <h3 class="text-accent text-lg font-semibold mb-2">
              {getOrderDisplayName(props.order)}
            </h3>
            <p class=" text-sm">
              {t("orders.accept.table_label")}{" "}
              {props.order.table?.name || "N/A"} |{" "}
              {t("orders.accept.amount_label")} {props.order.amount?.toFixed(2)}
            </p>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-accent text-sm font-medium mb-2">
                {t("orders.accept.prep_time")}
              </label>
              <div class="flex flex-wrap gap-2">
                {[15, 30, 45, 60, 90, 120, 180].map((minutes) => (
                  <button
                    type="button"
                    class={`btn ${
                      estimatedTime.value === minutes
                        ? "btn-active btn-primary"
                        : "btn-outline"
                    }`}
                    onClick={() => (estimatedTime.value = minutes)}
                  >
                    {minutes < 60
                      ? `${minutes}m`
                      : minutes % 60 === 0
                        ? `${minutes / 60}h`
                        : `${Math.floor(minutes / 60)}h ${minutes % 60}m`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </LemmonModal>
    );
  },
});
